import React, { useState, useMemo } from "react";
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Users,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Trash2,
  Trophy,
  Edit,
} from "../../../../components/icons";
import {
  MatchEvent,
  DuplicateHighlight,
} from "../../../../store/useMatchLogStore";
import { Match } from "../../types";
import { ACTION_FLOWS } from "../../constants";

interface LiveEventFeedProps {
  events: MatchEvent[];
  match: Match | null;
  duplicateHighlight?: DuplicateHighlight | null;
  onDeletePending?: (clientId: string) => void;
  onDeleteEvent?: (event: MatchEvent) => Promise<void> | void;
  canDeleteEvent?: (event: MatchEvent) => boolean;
  onUpdateEventNotes?: (event: MatchEvent, notes: string | null) => void;
  onUpdateEventData?: (
    event: MatchEvent,
    updates: Partial<MatchEvent>,
  ) => Promise<void> | void;
  t: any;
}

const EVENT_TYPE_ICONS: Record<string, React.ReactNode> = {
  Pass: <ArrowRight size={14} className="text-blue-500" />,
  Shot: <AlertCircle size={14} className="text-red-500" />,
  Goal: <Trophy size={14} className="text-amber-400" />,
  FoulCommitted: <XCircle size={14} className="text-yellow-600" />,
  Card: <AlertCircle size={14} className="text-red-600" />,
  Rebound: <Users size={14} className="text-green-600" />,
  Steal: <User size={14} className="text-purple-500" />,
  Block: <User size={14} className="text-indigo-500" />,
  Assist: <ArrowRight size={14} className="text-teal-500" />,
  Turnover: <XCircle size={14} className="text-orange-500" />,
  FreeThrow: <AlertCircle size={14} className="text-amber-500" />,
};

const getEventIcon = (event: MatchEvent, displayType: string) => {
  if (displayType === "Card") {
    const cardType = String(event.data?.card_type || "").toLowerCase();
    if (cardType.includes("white")) {
      return <CheckCircle size={14} className="text-slate-100" />;
    }
    if (cardType.includes("yellow")) {
      return <AlertCircle size={14} className="text-yellow-400" />;
    }
    return <AlertCircle size={14} className="text-red-500" />;
  }
  return (
    EVENT_TYPE_ICONS[displayType] || (
      <AlertCircle size={14} className="text-slate-500" />
    )
  );
};

const getOutcomeColor = (outcome?: string): string => {
  if (!outcome) return "text-gray-500";
  const lower = outcome.toLowerCase();
  if (
    lower.includes("complete") ||
    lower.includes("success") ||
    lower.includes("won")
  ) {
    return "text-green-400";
  }
  if (
    lower.includes("incomplete") ||
    lower.includes("failed") ||
    lower.includes("lost")
  ) {
    return "text-red-400";
  }
  if (lower.includes("goal")) {
    return "text-green-400 font-bold";
  }
  return "text-slate-400";
};

const getPlayerName = (
  playerId: string | undefined,
  match: Match | null,
): string => {
  if (!playerId || !match) return "Team Event";

  const allPlayers = [...match.home_team.players, ...match.away_team.players];

  const player = allPlayers.find((p) => p.id === playerId);
  return player ? `#${player.jersey_number} ${player.full_name}` : playerId;
};

const getTeamName = (teamId: string, match: Match | null): string => {
  if (!match) return "";
  if (teamId === match.home_team.id) return match.home_team.short_name;
  if (teamId === match.away_team.id) return match.away_team.short_name;
  return teamId;
};

const getDisplayTeamId = (event: MatchEvent): string | null => {
  if (event.team_id && event.team_id !== "NEUTRAL") return event.team_id;
  if (typeof event.data?.trigger_team_id === "string") {
    return event.data.trigger_team_id;
  }
  return event.team_id ?? null;
};

const getDisplayPlayerId = (event: MatchEvent): string | undefined => {
  return event.player_id ?? event.data?.trigger_player_id ?? undefined;
};

const formatEventDetails = (event: MatchEvent): string => {
  const details: string[] = [];

  if (event.data) {
    if (event.data.outcome) details.push(event.data.outcome);
    if (event.data.receiver_name) details.push(`→ ${event.data.receiver_name}`);
    if (event.data.pass_type) details.push(event.data.pass_type);
    if (event.data.shot_type) details.push(event.data.shot_type);
    if (event.data.card_type) details.push(event.data.card_type);
    if (event.data.set_piece_type) details.push(event.data.set_piece_type);
  }

  return details.join(" • ");
};

const getDisplayType = (event: MatchEvent): string => {
  if (event.type === "Shot" && event.data?.outcome === "Goal") {
    return "Goal";
  }
  return event.type;
};

export const LiveEventFeed: React.FC<LiveEventFeedProps> = ({
  events,
  match,
  duplicateHighlight,
  onDeletePending,
  onDeleteEvent,
  canDeleteEvent,
  onUpdateEventNotes,
  onUpdateEventData,
  t,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [deletingKeys, setDeletingKeys] = useState<Set<string>>(new Set());
  const [editingDataKey, setEditingDataKey] = useState<string | null>(null);
  const [editDrafts, setEditDrafts] = useState<
    Record<string, { outcome?: string }>
  >({});
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());

  const pendingCount = useMemo(
    () => events.filter((event) => event.client_id && !event._id).length,
    [events],
  );

  const reversedEvents = useMemo(() => {
    return [...events].reverse();
  }, [events]);

  const totalPages = Math.ceil(reversedEvents.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentEvents = reversedEvents.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page
  };

  const getEventKey = (event: MatchEvent) =>
    event._id || event.client_id || event.timestamp;

  const beginEditNotes = (event: MatchEvent) => {
    const key = getEventKey(event);
    setEditingKey(key);
    setNoteDrafts((prev) => ({
      ...prev,
      [key]: event.notes || "",
    }));
  };

  const cancelEditNotes = () => {
    setEditingKey(null);
  };

  const saveNotes = (event: MatchEvent) => {
    if (!onUpdateEventNotes) return;
    const key = getEventKey(event);
    const raw = noteDrafts[key] ?? "";
    const next = raw.trim();
    onUpdateEventNotes(event, next ? next : null);
    setEditingKey(null);
  };

  const clearNotes = (event: MatchEvent) => {
    if (!onUpdateEventNotes) return;
    onUpdateEventNotes(event, null);
    setEditingKey(null);
  };

  const handleDeleteEvent = async (event: MatchEvent) => {
    if (!onDeleteEvent) return;
    const key = getEventKey(event);
    if (deletingKeys.has(key)) return;
    setDeletingKeys((prev) => new Set(prev).add(key));
    try {
      await onDeleteEvent(event);
    } finally {
      setDeletingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  return (
    <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-100">
            {t("liveEvents", "Live Events")}
          </h2>
          <div className="flex items-center gap-2">
            <span
              className="text-sm font-medium text-slate-400 bg-slate-700 px-3 py-1 rounded-full border border-slate-600"
              data-testid="live-events-total"
              data-count={events.length}
            >
              {events.length} {t("eventsLabel", "events")}
            </span>
            {pendingCount > 0 && (
              <span
                className="text-xs font-semibold text-amber-300 bg-amber-900/30 px-2 py-1 rounded-full border border-amber-700/50"
                title={t("pendingEvents", "Events awaiting confirmation")}
              >
                {t("pending", "Pending")}: {pendingCount}
              </span>
            )}
          </div>
        </div>

        {/* Page Size Selector */}
        {events.length > 10 && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-slate-400">{t("show", "Show")}:</span>
            {[10, 20, 50, 100].map((size) => (
              <button
                key={size}
                onClick={() => handlePageSizeChange(size)}
                className={`text-xs px-2 py-1 rounded ${
                  pageSize === size
                    ? "bg-blue-900/40 text-blue-300 font-medium border border-blue-800"
                    : "text-slate-500 hover:bg-slate-700"
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Events List */}
      <div className="space-y-1 flex-1 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
        {events.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-12">
            <Users size={48} className="mx-auto mb-3 text-slate-700" />
            <p>{t("noEvents", "No events yet")}</p>
            <p className="text-xs mt-1">
              {t("startLogging", "Start logging to see events appear here")}
            </p>
          </div>
        ) : (
          currentEvents.map((event, index) => {
            const isHighlighted =
              !!duplicateHighlight &&
              duplicateHighlight.match_clock === event.match_clock &&
              duplicateHighlight.period === event.period &&
              duplicateHighlight.team_id === event.team_id;

            const outcomeColor = getOutcomeColor(event.data?.outcome);
            const eventDetails = formatEventDetails(event);
            const displayPlayerId = getDisplayPlayerId(event);
            const playerName = getPlayerName(displayPlayerId, match);
            const displayTeamId = getDisplayTeamId(event);
            const teamName = displayTeamId
              ? getTeamName(displayTeamId, match)
              : "";
            const displayType = getDisplayType(event);
            const eventKey = getEventKey(event);
            const isEditing = editingKey === eventKey;
            const noteDraft = noteDrafts[eventKey] ?? event.notes ?? "";
            const canDelete =
              typeof onDeleteEvent === "function" &&
              (canDeleteEvent ? canDeleteEvent(event) : Boolean(event._id));
            const deleteDisabled = deletingKeys.has(eventKey);

            return (
              <div
                key={`${getEventKey(event)}-${startIndex + index}`}
                data-testid="live-event-item"
                className={`border rounded-lg p-3 transition-all hover:shadow-sm ${
                  isHighlighted
                    ? "border-amber-500/50 bg-amber-900/20 shadow-md ring-1 ring-amber-500/50"
                    : "border-slate-700 hover:border-slate-600 bg-slate-900/50"
                }`}
              >
                {/* Header Row */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {getEventIcon(event, displayType)}
                    <span className="font-semibold text-sm text-slate-200 truncate">
                      {displayType}
                    </span>
                    {displayType === "Goal" && (
                      <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full border border-amber-500/40">
                        {t("goalIndicator", "Goal")}
                      </span>
                    )}
                    {teamName && (
                      <span className="text-xs bg-slate-800 text-slate-400 px-2 py-0.5 rounded font-medium border border-slate-700">
                        {teamName}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-slate-500 font-mono">
                      P{event.period}
                    </span>
                    <span className="text-sm font-mono font-semibold text-slate-300">
                      {event.match_clock}
                    </span>
                    {onUpdateEventData && event._id && (
                      <button
                        type="button"
                        data-testid="event-edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          const key = getEventKey(event);
                          if (editingDataKey === key) {
                            setEditingDataKey(null);
                          } else {
                            setEditingDataKey(key);
                            setEditDrafts((prev) => ({
                              ...prev,
                              [key]: { outcome: event.data?.outcome || "" },
                            }));
                          }
                        }}
                        className="text-slate-400 hover:text-blue-300 p-1 rounded hover:bg-slate-800"
                        title={t("editEvent", "Edit event")}
                      >
                        <Edit size={14} />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        data-testid="event-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteEvent(event);
                        }}
                        className="text-slate-400 hover:text-rose-300 p-1 rounded hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t("deleteEvent", "Delete event")}
                        disabled={deleteDisabled}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Player Info */}
                {displayPlayerId && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <User size={12} className="text-slate-500" />
                    <span className="text-xs text-slate-300 font-medium">
                      {playerName}
                    </span>
                  </div>
                )}

                {/* Event Details */}
                {eventDetails && (
                  <div className="flex items-center gap-1 mt-2">
                    {event.data?.outcome === "Complete" && (
                      <CheckCircle size={12} className="text-green-500" />
                    )}
                    {event.data?.outcome === "Incomplete" && (
                      <XCircle size={12} className="text-red-500" />
                    )}
                    {event.data?.outcome === "Goal" && (
                      <CheckCircle size={12} className="text-green-500" />
                    )}
                    <span className={`text-xs ${outcomeColor}`}>
                      {eventDetails}
                    </span>
                  </div>
                )}

                {/* Inline Edit Panel */}
                {editingDataKey === eventKey &&
                  onUpdateEventData &&
                  (() => {
                    const draft = editDrafts[eventKey] ?? {
                      outcome: event.data?.outcome || "",
                    };
                    const isSaving = savingKeys.has(eventKey);
                    // Find available outcomes for this event type from ACTION_FLOWS
                    const flowConfig = ACTION_FLOWS[event.type];
                    const availableOutcomes: string[] = flowConfig
                      ? Object.values(flowConfig.outcomes ?? {}).flat()
                      : [];
                    const uniqueOutcomes = [...new Set(availableOutcomes)];

                    return (
                      <div
                        className="mt-2 p-2 bg-slate-900/80 border border-blue-800/40 rounded-lg space-y-2"
                        data-testid="event-edit-panel"
                      >
                        {/* Outcome Editor */}
                        {uniqueOutcomes.length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wide text-slate-500 w-16 flex-shrink-0">
                              {t("outcome", "Outcome")}
                            </span>
                            <select
                              className="flex-1 text-xs bg-slate-800 text-slate-200 border border-slate-700 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              data-testid="event-edit-outcome"
                              value={draft.outcome || ""}
                              onChange={(e) =>
                                setEditDrafts((prev) => ({
                                  ...prev,
                                  [eventKey]: {
                                    ...draft,
                                    outcome: e.target.value,
                                  },
                                }))
                              }
                            >
                              <option value="">
                                {t("selectOutcome", "— Select —")}
                              </option>
                              {uniqueOutcomes.map((o) => (
                                <option key={o} value={o}>
                                  {o}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {/* Save / Cancel */}
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            data-testid="event-edit-save"
                            disabled={isSaving}
                            className="text-xs px-3 py-1 rounded bg-blue-600/30 text-blue-200 border border-blue-500/40 hover:bg-blue-600/50 disabled:opacity-50"
                            onClick={async () => {
                              setSavingKeys((prev) =>
                                new Set(prev).add(eventKey),
                              );
                              try {
                                const updates: Partial<MatchEvent> = {};
                                if (
                                  draft.outcome !== (event.data?.outcome || "")
                                ) {
                                  updates.data = {
                                    ...event.data,
                                    outcome: draft.outcome || undefined,
                                  };
                                }
                                if (Object.keys(updates).length > 0) {
                                  await onUpdateEventData(event, updates);
                                }
                                setEditingDataKey(null);
                              } finally {
                                setSavingKeys((prev) => {
                                  const next = new Set(prev);
                                  next.delete(eventKey);
                                  return next;
                                });
                              }
                            }}
                          >
                            {isSaving
                              ? t("saving", "Saving…")
                              : t("save", "Save")}
                          </button>
                          <button
                            type="button"
                            data-testid="event-edit-cancel"
                            className="text-xs px-3 py-1 rounded bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700"
                            onClick={() => setEditingDataKey(null)}
                          >
                            {t("cancel", "Cancel")}
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                {/* Notes */}
                <div className="mt-2">
                  {isEditing ? (
                    <div className="space-y-2">
                      <textarea
                        className="w-full text-xs text-slate-100 bg-slate-900/60 border border-slate-700 rounded p-2 focus:outline-none focus:ring-1 focus:ring-amber-400"
                        data-testid="event-note-textarea"
                        rows={2}
                        value={noteDraft}
                        onChange={(e) =>
                          setNoteDrafts((prev) => ({
                            ...prev,
                            [eventKey]: e.target.value,
                          }))
                        }
                        placeholder={t("notesPlaceholder", "Add a note...")}
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          data-testid="event-note-save"
                          className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-200 border border-amber-500/40"
                          onClick={() => saveNotes(event)}
                        >
                          {t("save", "Save")}
                        </button>
                        <button
                          type="button"
                          data-testid="event-note-cancel"
                          className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-300 border border-slate-700"
                          onClick={cancelEditNotes}
                        >
                          {t("cancel", "Cancel")}
                        </button>
                        <button
                          type="button"
                          data-testid="event-note-remove"
                          className="text-xs px-2 py-1 rounded bg-rose-500/20 text-rose-200 border border-rose-500/40"
                          onClick={() => clearNotes(event)}
                        >
                          {t("remove", "Remove")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-xs text-slate-300">
                        {event.notes ? (
                          <span className="bg-slate-800/60 border border-slate-700 rounded px-2 py-1 inline-block">
                            {event.notes}
                          </span>
                        ) : (
                          <span className="text-slate-500">
                            {t("notesEmpty", "No notes")}
                          </span>
                        )}
                      </div>
                      {onUpdateEventNotes && (
                        <button
                          type="button"
                          data-testid="event-note-edit"
                          className="text-[10px] uppercase tracking-wide text-amber-300 hover:text-amber-200"
                          onClick={() => beginEditNotes(event)}
                        >
                          {event.notes
                            ? t("edit", "Edit")
                            : t("addNote", "Add note")}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Status Indicators */}
                <div className="flex items-center gap-2 mt-2">
                  {event._confirmed && (
                    <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1 border border-green-900/50">
                      <CheckCircle size={10} />
                      {t("confirmed", "Confirmed")}
                    </span>
                  )}
                  {event.client_id && !event._id && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-yellow-900/30 text-yellow-500 px-2 py-0.5 rounded-full border border-yellow-900/50">
                        {t("pending", "Pending")}
                      </span>
                      {onDeletePending && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeletePending(event.client_id!);
                          }}
                          className="text-slate-500 hover:text-red-400 p-0.5 rounded hover:bg-slate-800 transition-colors"
                          title={t("deletePending", "Delete pending event")}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  )}
                  {isHighlighted && (
                    <span className="text-xs bg-amber-900/40 text-amber-400 px-2 py-0.5 rounded-full font-medium border border-amber-700/50">
                      {t("duplicate", "Duplicate")}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="border-t border-slate-700 p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500">
              {t("showing", "Showing")} {startIndex + 1}-
              {Math.min(endIndex, reversedEvents.length)} {t("of", "of")}{" "}
              {reversedEvents.length}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-200"
                title={t("firstPage", "First page")}
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1 rounded hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-200"
                title={t("previousPage", "Previous page")}
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs font-medium text-slate-300 px-3">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1 rounded hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-200"
                title={t("nextPage", "Next page")}
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1 rounded hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-400 hover:text-slate-200"
                title={t("lastPage", "Last page")}
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveEventFeed;
